import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, Check, CircleCheck, Pencil } from "lucide-react";
import { useState } from "react";
import { ErrorState } from "@/components/brand/error-state";
import { Eyebrow } from "@/components/brand/eyebrow";
import { PageHeader } from "@/components/layout/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { isEnabled } from "@/config/features";
import { useResolveComment } from "@/features/brand-review";
import { SECTION_BY_KEY, type SectionKey } from "@/features/brand-templates";
import { InviteSmmDialog } from "@/features/invitations";
import { MentionsCard } from "@/features/mentions";
import { CloseOrgCard } from "@/features/offboarding";
import { EditOrgDialog, SetupChecklist } from "@/features/orgs";
import { PublishReportCard } from "@/features/reports";
import {
  brandReviewOptions,
  invitationsCreateMutation,
  invitationsListOptions,
  invitationsListQueryKey,
  invitationsRevokeMutation,
  orgsGetOptions,
  type ReviewCommentPublic,
  usersListOptions,
} from "@/lib/api";

/**
 * One client, from The Motherhood's side.
 *
 * Two jobs: show how far their setup has got and what to do next, and carry the
 * other half of the review loop, where the client's comments land and get
 * closed off.
 *
 * Reads inside the client organisation by passing X-Org-Id, which is how a
 * Super Admin acts on an org's behalf.
 */
export const Route = createFileRoute("/_authenticated/admin/orgs/$orgId/")({
  component: OrgDetailPage,
});

function OrgDetailPage() {
  const { orgId } = Route.useParams();
  const headers = { "X-Org-Id": orgId };
  const queryClient = useQueryClient();

  const org = useQuery(orgsGetOptions({ path: { org_id: orgId } }));
  const review = useQuery({ ...brandReviewOptions({ headers }), meta: { silent: true } });
  const invitations = useQuery({
    ...invitationsListOptions({ headers }),
    meta: { silent: true },
  });
  // Whether their manager is already in, invitation or not.
  const members = useQuery({
    ...usersListOptions({ headers, query: { limit: 200 } }),
    meta: { silent: true },
  });
  const manager = members.data?.items.find((u) => u.role === "smm") ?? null;

  const [editing, setEditing] = useState(false);
  const [inviting, setInviting] = useState(false);
  const resolve = useResolveComment();

  const refreshInvitations = () =>
    queryClient.invalidateQueries({ queryKey: invitationsListQueryKey() });

  const resend = useMutation({
    ...invitationsCreateMutation(),
    onSuccess: refreshInvitations,
  });
  const revoke = useMutation({
    ...invitationsRevokeMutation(),
    onSuccess: refreshInvitations,
  });

  if (org.isPending) return <Skeleton className="h-96 rounded-lg" />;
  if (org.isError) return <ErrorState onRetry={() => void org.refetch()} />;

  const comments = review.data?.comments ?? [];
  const open = comments.filter((c) => c.status === "open");
  const approved = review.data?.approvalStatus === "approved";
  const closed = Boolean(org.data.archivedAt || org.data.removedAt);

  return (
    <>
      <Button asChild variant="ghost" size="sm" className="mb-4 -ml-2">
        <Link to="/admin/orgs">
          <ArrowLeft /> All organizations
        </Link>
      </Button>

      <PageHeader
        eyebrow={org.data.industry ?? "Client"}
        title={org.data.name}
        description={
          org.data.removedAt
            ? "Removed. Nobody in this org can sign in, and the data is deleted after 90 days."
            : org.data.archivedAt
              ? "Archived. Nobody in this org can sign in."
              : statusLine(org.data.status, approved, open.length)
        }
        actions={
          <>
            <Button variant="outline" onClick={() => setEditing(true)}>
              <Pencil /> Edit details
            </Button>
            {approved ? (
              <Badge variant="success">
                <CircleCheck /> Approved
              </Badge>
            ) : open.length > 0 ? (
              <Badge variant="destructive">{open.length} open</Badge>
            ) : (
              <Badge>Setting up</Badge>
            )}
          </>
        }
      />

      <div className="space-y-6">
        {closed ? <CloseOrgCard org={org.data} /> : null}
        {invitations.isPending || review.isPending ? (
          <Skeleton className="h-64 rounded-lg" />
        ) : (
          <SetupChecklist
            orgId={orgId}
            review={review.data}
            invitations={invitations.data ?? []}
            manager={manager}
            busy={resend.isPending || revoke.isPending}
            onInvite={() => setInviting(true)}
            onResend={(invitation) =>
              resend.mutate({
                headers,
                body: {
                  email: invitation.email,
                  name: invitation.name,
                  role: invitation.role,
                },
              })
            }
            onRevoke={(invitation) =>
              revoke.mutate({ headers, path: { invitation_id: invitation.id } })
            }
          />
        )}

        <PublishReportCard orgId={orgId} orgName={org.data.name} />
        {isEnabled("brandMentionTracking") ? <MentionsCard orgId={orgId} /> : null}

        <section>
          <h2 className="mb-3 font-semibold text-foreground text-lg">What they have told you</h2>
          {comments.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center">
                <p className="font-semibold text-foreground">No feedback from the client</p>
                <p className="mt-1 text-muted-foreground text-sm">
                  {approved
                    ? "They read the setup and approved it."
                    : "Anything they disagree with will appear here."}
                </p>
              </CardContent>
            </Card>
          ) : (
            <ul className="space-y-3">
              {comments.map((comment) => (
                <CommentRow
                  key={comment.id}
                  comment={comment}
                  busy={resolve.isPending}
                  onResolve={() => resolve.mutate({ path: { comment_id: comment.id }, headers })}
                />
              ))}
            </ul>
          )}
        </section>

        {closed ? null : <CloseOrgCard org={org.data} />}
      </div>

      <EditOrgDialog org={org.data} open={editing} onOpenChange={setEditing} />
      <InviteSmmDialog
        open={inviting}
        onOpenChange={setInviting}
        orgId={orgId}
        orgName={org.data.name}
      />
    </>
  );
}

function statusLine(status: string, approved: boolean, openCount: number): string {
  if (approved) return "Live. Their manager has approved the brand setup.";
  if (openCount > 0) return "They have sent feedback that is waiting on you.";
  if (status === "paused") return "Paused.";
  return "Still being set up. The steps below are what is left.";
}

function CommentRow({
  comment,
  busy,
  onResolve,
}: {
  comment: ReviewCommentPublic;
  busy: boolean;
  onResolve: () => void;
}) {
  const section = comment.section ? SECTION_BY_KEY[comment.section as SectionKey] : undefined;
  const isOpen = comment.status === "open";

  return (
    <li className="rounded-lg border border-border bg-card p-4 shadow-soft">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <Eyebrow>{section?.label ?? "The setup overall"}</Eyebrow>
          <p className="mt-1.5 text-foreground">{comment.body}</p>
          <p className="mt-2 text-muted-foreground text-xs">
            {comment.authorName ?? "The client"} on{" "}
            {new Date(comment.createdAt).toLocaleDateString()}
          </p>
        </div>
        {isOpen ? (
          <Button size="sm" variant="outline" disabled={busy} onClick={onResolve}>
            <Check /> Mark as done
          </Button>
        ) : (
          <Badge variant="success">Done</Badge>
        )}
      </div>
    </li>
  );
}
